import {
  OFFICIAL_PROXY_TOLERANCE_PCT,
  type ShipmentCandidateLineItem,
} from '@crv/contracts/shipments';

export interface ShipmentPreviewDailyRow {
  date: string;
  swCrvLb: number;
  sclCount: number;
  scsCount: number;
  sc25Count: number;
  scrapLb: number;
  refundValueCents: number;
  redemptionWeightLb: number;
  totalWeightLb: number;
  purchaseCount: number;
  lineCount: number;
  lines: ShipmentCandidateLineItem[];
}

export interface ShipmentRangePlanningSummary {
  operationalLossPct: number;
  officialProxyTolerancePct: number;
  rangeTotalWeightLb: number;
  rangeRedemptionWeightLb: number;
  rangeSwCrvLb: number;
  rangeScrapWeightLb: number;
  refundValueCents: number;
  purchaseCount: number;
  lineCount: number;
  minimumLoadNeededLb: number;
  official2_5ReferenceLb: number;
}

export interface BuildShipmentRangePreviewInput {
  rangeCandidates: ShipmentCandidateLineItem[] | null;
  timeZone: string;
  effectiveRangeStartAt: string | null;
  effectiveRangeEndAt: string | null;
  operationalLossPct: number;
}

export interface ShipmentRangePreview {
  dailyRows: ShipmentPreviewDailyRow[];
  summary: ShipmentRangePlanningSummary | null;
}

interface DailyPreviewAccumulator {
  date: string;
  sclCount: number;
  scsCount: number;
  sc25Count: number;
  lineCount: number;
  purchaseIds: Set<string>;
  lines: ShipmentCandidateLineItem[];
}

const SCL_DEPOSIT_CENTS = 10;
const SCS_DEPOSIT_CENTS = 5;
const SC25_DEPOSIT_CENTS = 25;

export function buildShipmentRangePreview(
  input: BuildShipmentRangePreviewInput,
): ShipmentRangePreview {
  if (!input.rangeCandidates) {
    return { dailyRows: [], summary: null };
  }

  const effectiveRangeStartMs = parseIsoToMs(input.effectiveRangeStartAt);
  const effectiveRangeEndMs = parseIsoToMs(input.effectiveRangeEndAt);
  if (
    effectiveRangeStartMs === null ||
    effectiveRangeEndMs === null ||
    effectiveRangeStartMs >= effectiveRangeEndMs
  ) {
    return { dailyRows: [], summary: null };
  }

  const grouped = new Map<string, DailyPreviewAccumulator>();

  for (const line of input.rangeCandidates) {
    if (!isLineWithinEffectiveRange(line.occurredAt, effectiveRangeStartMs, effectiveRangeEndMs)) {
      continue;
    }

    const date = formatDateInTimeZone(line.occurredAt, input.timeZone) ?? 'Invalid date';
    const existing = grouped.get(date) ?? {
      date,
      sclCount: 0,
      scsCount: 0,
      sc25Count: 0,
      lineCount: 0,
      purchaseIds: new Set<string>(),
      lines: [],
    };

    existing.lineCount += 1;
    existing.purchaseIds.add(line.purchaseCommittedId);
    existing.lines.push(line);

    if (line.presentationType === 'SCL') {
      existing.sclCount += line.pieces ?? 0;
    }
    if (line.presentationType === 'SCS') {
      existing.scsCount += line.pieces ?? 0;
    }
    if (line.presentationType === 'SC25') {
      existing.sc25Count += line.pieces ?? 0;
    }
    grouped.set(date, existing);
  }

  for (const value of grouped.values()) {
    value.lines.sort(compareLinesByOccurredAtThenId);
  }

  const dailyRows = Array.from(grouped.values())
    .map((value) => ({
      ...calculateDailyTotals(value.lines),
      date: value.date,
      sclCount: value.sclCount,
      scsCount: value.scsCount,
      sc25Count: value.sc25Count,
      purchaseCount: value.purchaseIds.size,
      lineCount: value.lineCount,
      lines: value.lines,
    }))
    .sort((left, right) => left.date.localeCompare(right.date));

  return {
    dailyRows,
    summary: buildSummary(dailyRows, input.operationalLossPct),
  };
}

function buildSummary(
  dailyRows: ShipmentPreviewDailyRow[],
  operationalLossPct: number,
): ShipmentRangePlanningSummary | null {
  if (dailyRows.length === 0) {
    return null;
  }

  const normalizedOperationalLossPct = Math.max(0, operationalLossPct);
  const purchaseIds = new Set<string>();
  let rangeTotalWeightLb = 0;
  let rangeRedemptionWeightLb = 0;
  let rangeSwCrvLb = 0;
  let rangeScrapWeightLb = 0;
  let refundValueCents = 0;
  let lineCount = 0;

  for (const row of dailyRows) {
    rangeTotalWeightLb += row.totalWeightLb;
    rangeRedemptionWeightLb += row.redemptionWeightLb;
    rangeSwCrvLb += row.swCrvLb;
    rangeScrapWeightLb += row.scrapLb;
    refundValueCents += row.refundValueCents;
    lineCount += row.lineCount;

    for (const line of row.lines) {
      purchaseIds.add(line.purchaseCommittedId);
    }
  }

  return {
    operationalLossPct: normalizedOperationalLossPct,
    officialProxyTolerancePct: OFFICIAL_PROXY_TOLERANCE_PCT,
    rangeTotalWeightLb,
    rangeRedemptionWeightLb,
    rangeSwCrvLb,
    rangeScrapWeightLb,
    refundValueCents,
    purchaseCount: purchaseIds.size,
    lineCount,
    minimumLoadNeededLb: rangeTotalWeightLb / (1 + (normalizedOperationalLossPct / 100)),
    official2_5ReferenceLb: rangeTotalWeightLb / (1 + (OFFICIAL_PROXY_TOLERANCE_PCT / 100)),
  };
}

function parseIsoToMs(value: string | null): number | null {
  if (!value) {
    return null;
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function isLineWithinEffectiveRange(
  occurredAt: string,
  effectiveRangeStartMs: number,
  effectiveRangeEndMs: number,
): boolean {
  const occurredAtMs = parseIsoToMs(occurredAt);
  if (occurredAtMs === null) {
    return false;
  }

  return occurredAtMs >= effectiveRangeStartMs && occurredAtMs < effectiveRangeEndMs;
}

function compareLinesByOccurredAtThenId(
  left: ShipmentCandidateLineItem,
  right: ShipmentCandidateLineItem,
): number {
  const leftOccurredAtMs = parseIsoToMs(left.occurredAt);
  const rightOccurredAtMs = parseIsoToMs(right.occurredAt);

  if (leftOccurredAtMs === null && rightOccurredAtMs === null) {
    return left.lineItemId.localeCompare(right.lineItemId);
  }
  if (leftOccurredAtMs === null) {
    return 1;
  }
  if (rightOccurredAtMs === null) {
    return -1;
  }

  const timeDelta = leftOccurredAtMs - rightOccurredAtMs;
  if (timeDelta !== 0) {
    return timeDelta;
  }

  return left.lineItemId.localeCompare(right.lineItemId);
}

function calculateDailyTotals(lines: ShipmentCandidateLineItem[]): {
  swCrvLb: number;
  scrapLb: number;
  refundValueCents: number;
  redemptionWeightLb: number;
  totalWeightLb: number;
} {
  let scrapLb = 0;
  let fallbackRedemptionWeightLb = 0;
  let fallbackScEquivalentLbs = 0;
  let fallbackRefundValueCents = 0;
  let baseSwPayoutCents = 0;
  let scMoneyCents = 0;
  let resolvedBaseSwRatePerLb: number | null = null;
  let qualityDiscountMoneyCentsTotal = 0;

  for (const line of lines) {
    if (line.presentationType === 'SP') {
      scrapLb += line.netWeightLbs;
      continue;
    }

    if (!line.isCrvEligible) {
      continue;
    }

    fallbackRedemptionWeightLb += line.equivalentLbs ?? 0;
    fallbackRefundValueCents += line.lineTotalCents;

    switch (line.presentationType) {
      case 'SW': {
        const baseRatePerLb = line.baseRatePerLb;
        if (typeof baseRatePerLb === 'number' && Number.isFinite(baseRatePerLb) && baseRatePerLb > 0) {
          resolvedBaseSwRatePerLb ??= baseRatePerLb;
          baseSwPayoutCents += toCents(line.netWeightLbs * baseRatePerLb);
        }
        break;
      }
      case 'SCL': {
        scMoneyCents += (line.pieces ?? 0) * SCL_DEPOSIT_CENTS;
        fallbackScEquivalentLbs += line.equivalentLbs ?? 0;
        break;
      }
      case 'SCS': {
        scMoneyCents += (line.pieces ?? 0) * SCS_DEPOSIT_CENTS;
        fallbackScEquivalentLbs += line.equivalentLbs ?? 0;
        break;
      }
      case 'SC25': {
        scMoneyCents += (line.pieces ?? 0) * SC25_DEPOSIT_CENTS;
        fallbackScEquivalentLbs += line.equivalentLbs ?? 0;
        break;
      }
      default:
        break;
    }

    if (line.qualityDiscountMoneyCents > 0) {
      qualityDiscountMoneyCentsTotal += line.qualityDiscountMoneyCents;
    }
  }

  const hasResolvedBaseRate =
    typeof resolvedBaseSwRatePerLb === 'number'
    && Number.isFinite(resolvedBaseSwRatePerLb)
    && resolvedBaseSwRatePerLb > 0;
  const baseSwRatePerLb = hasResolvedBaseRate ? (resolvedBaseSwRatePerLb as number) : 0;
  const refundValueCents = hasResolvedBaseRate
    ? Math.max(0, baseSwPayoutCents + scMoneyCents - qualityDiscountMoneyCentsTotal)
    : fallbackRefundValueCents;
  const redemptionWeightLb = hasResolvedBaseRate
    ? (refundValueCents / 100) / baseSwRatePerLb
    : fallbackRedemptionWeightLb;
  const scEquivalentLbs = hasResolvedBaseRate
    ? (scMoneyCents / 100) / baseSwRatePerLb
    : fallbackScEquivalentLbs;
  const swCrvLb = Math.max(0, redemptionWeightLb - scEquivalentLbs);

  return {
    swCrvLb: hasResolvedBaseRate ? swCrvLb : Math.max(0, fallbackRedemptionWeightLb - fallbackScEquivalentLbs),
    scrapLb,
    refundValueCents,
    redemptionWeightLb,
    totalWeightLb: redemptionWeightLb + scrapLb,
  };
}

function toCents(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.round((value + Number.EPSILON) * 100);
}

function formatDateInTimeZone(iso: string, timeZone: string): string | null {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(parsed);
  const getPart = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((candidate) => candidate.type === type)?.value ?? '';

  return `${getPart('year')}-${getPart('month')}-${getPart('day')}`;
}
